/**
 * Simple API response caching utility
 * Stores responses in localStorage with TTL
 */

const CACHE_PREFIX = 'api_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

export const apiCache = {
  /**
   * Get cached data if it exists and hasn't expired
   */
  get: (key) => {
    try {
      const cached = localStorage.getItem(CACHE_PREFIX + key);
      if (!cached) return null;

      const { data, expiry } = JSON.parse(cached);
      if (Date.now() > expiry) {
        // Expired - remove it
        localStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }

      console.log(`✅ Cache HIT: ${key}`);
      return data;
    } catch (error) {
      console.error('Cache get error:', error);
      return null;
    }
  },

  /**
   * Store data in cache with TTL
   */
  set: (key, data, ttl = DEFAULT_TTL) => {
    try {
      const cacheData = {
        data,
        expiry: Date.now() + ttl
      };
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheData));
      console.log(`💾 Cache SET: ${key} (TTL: ${ttl / 1000}s)`);
    } catch (error) {
      console.error('Cache set error:', error);
      // If localStorage is full, clear old cache
      if (error.name === 'QuotaExceededError') {
        apiCache.clearExpired();
        try {
          localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ data, expiry: Date.now() + ttl }));
        } catch (e) {
          console.error('Cache still full after cleanup');
        }
      }
    }
  },

  /**
   * Invalidate a specific cache entry
   */
  invalidate: (key) => {
    localStorage.removeItem(CACHE_PREFIX + key);
    console.log(`🗑️ Cache INVALIDATE: ${key}`);
  },

  /**
   * Clear all expired cache entries
   */
  clearExpired: () => {
    const keys = Object.keys(localStorage);
    let cleared = 0;
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (Date.now() > cached.expiry) {
            localStorage.removeItem(key);
            cleared++;
          }
        } catch (e) {
          // Invalid cache entry, remove it
          localStorage.removeItem(key);
          cleared++;
        }
      }
    });
    
    if (cleared > 0) {
      console.log(`🧹 Cleared ${cleared} expired cache entries`);
    }
  },

  /**
   * Clear all cache
   */
  clearAll: () => {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log('🗑️ All cache cleared');
  }
};

// Clear expired cache on load
apiCache.clearExpired();
